import React, { useState } from "react";
import { Modal, DataTable as Table, Button } from "../../../components/ui";
import toast from "react-hot-toast";
import {
  X,
  Check,
  AlertTriangle,
} from "lucide-react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import api from "../../../services/api";
import { fmt, fmtCurrency } from "../../../lib/formatters";

export const BlumPurchasesBulkUpload = ({
  isOpen,
  onClose,
  initialFile,
  onSuccess
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // New State for Intelligent Detection
  const [detectedType, setDetectedType] = useState(null);
  const [needsManualSelection, setNeedsManualSelection] = useState(false);
  const [rawRowsArray, setRawRowsArray] = useState([]);
  const [detectedHeaderIndex, setDetectedHeaderIndex] = useState(0);

  React.useEffect(() => {
    if (isOpen && initialFile) {
      setFile(initialFile);
      parseFile(initialFile);
      setErrors([]);
    }
  }, [isOpen, initialFile]);

  const parseFile = (f) => {
    const isExcel = f.name.endsWith(".xlsx") || f.name.endsWith(".xls");

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          
          // 1. Get raw values with formatting (raw: false) to preserve currency symbols and commas
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
            header: 1,
            defval: "",
            raw: false,
          });
          
          // 2. Extract image formulas or hyperlinks, as sheet_to_json ignores them
          const sheet = workbook.Sheets[sheetName];
          const range = XLSX.utils.decode_range(sheet['!ref']);
          
          for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellRef = XLSX.utils.encode_cell({c: C, r: R});
              const cell = sheet[cellRef];
              
              if (!cell) continue;
              
              // Handle IMAGE formula or Place-in-Cell (_xlfn.DISPIMG)
              if (cell.f) {
                const upperF = cell.f.toUpperCase();
                if (upperF.startsWith("IMAGE")) {
                  const match = cell.f.match(/IMAGE\(\s*"([^"]+)"/i);
                  if (match && rows[R]) rows[R][C] = match[1];
                  continue;
                }
                if (upperF.includes("DISPIMG")) {
                  if (rows[R]) rows[R][C] = "HAS_IMAGE"; // Mark as having an image
                  continue;
                }
              }
              
              // Handle Hyperlink
              if (cell.l && cell.l.Target && rows[R]) {
                 rows[R][C] = cell.l.Target; // Override cell with hyperlink URL
              }
            }
          }
          
          // 3. Extract Embedded Binary Images manually using JSZip (Bypass ExcelJS bug)
          try {
            console.group("--- EXCEL IMAGE EXTRACTION DEBUGGING (JSZip) ---");
            console.log("1. Loading JSZip...");
            const JSZip = require("jszip"); // Using require since it's commonjs or import if available
            const zip = await JSZip.loadAsync(data);
            console.log("1a. ZIP loaded successfully.");
            
            // Log media count
            let mediaCount = 0;
            zip.forEach((relativePath) => {
              if (relativePath.startsWith('xl/media/')) mediaCount++;
            });
            console.log(`2. xl/media/ file count: ${mediaCount}`);

            let mappedCount = 0;
            let failedMappings = [];
            
            // Extract relationships
            const relsFile = zip.file("xl/drawings/_rels/drawing1.xml.rels");
            const drawingFile = zip.file("xl/drawings/drawing1.xml");
            
            if (drawingFile && relsFile) {
              const relsXml = await relsFile.async("string");
              const drawingXml = await drawingFile.async("string");
              
              const relMap = {};
              const relRegex = /<Relationship([^>]+)>/g;
              let relMatch;
              while ((relMatch = relRegex.exec(relsXml)) !== null) {
                const attrs = relMatch[1];
                const idMatch = attrs.match(/Id="([^"]+)"/);
                const targetMatch = attrs.match(/Target="([^"]+)"/);
                if (idMatch && targetMatch) {
                  let target = targetMatch[1];
                  if (target.startsWith('../')) target = target.replace('../', 'xl/');
                  if (target.startsWith('/xl/')) target = target.substring(1);
                  relMap[idMatch[1]] = target;
                }
              }
              console.log(`3. Found ${Object.keys(relMap).length} relationships in drawing1.xml.rels`);
              
              const anchors = drawingXml.split(/<xdr:oneCellAnchor>|<oneCellAnchor>|<xdr:twoCellAnchor>|<twoCellAnchor>/);
              console.log(`3a. Found ${anchors.length - 1} anchors in drawing1.xml`);
              
              for (let i = 1; i < anchors.length; i++) {
                const anchor = anchors[i];
                const colMatch = anchor.match(/<xdr:col>(\d+)<\/xdr:col>|<col>(\d+)<\/col>/);
                const rowMatch = anchor.match(/<xdr:row>(\d+)<\/xdr:row>|<row>(\d+)<\/row>/);
                const embedMatch = anchor.match(/<a:blip[^>]+r:embed="([^"]+)"/);
                
                const col = colMatch ? parseInt(colMatch[1] || colMatch[2]) : null;
                const row = rowMatch ? parseInt(rowMatch[1] || rowMatch[2]) : null;
                const embedId = embedMatch ? embedMatch[1] : null;
                
                if (col !== null && row !== null && embedId && relMap[embedId]) {
                  const imagePath = relMap[embedId];
                  const imgFile = zip.file(imagePath);
                  
                  if (imgFile) {
                    const imgBuffer = await imgFile.async("uint8array");
                    
                    let binary = '';
                    for (let b = 0; b < imgBuffer.byteLength; b++) {
                      binary += String.fromCharCode(imgBuffer[b]);
                    }
                    const base64 = window.btoa(binary);
                    
                    const extension = imagePath.split('.').pop().toLowerCase();
                    const dataUrl = `data:image/${extension};base64,${base64}`;
                    
                    if (rows[row]) {
                      while (rows[row].length <= col) {
                        rows[row].push("");
                      }
                      rows[row][col] = dataUrl;
                      mappedCount++;
                    } else {
                      failedMappings.push(`Row ${row}`);
                    }
                  } else {
                    failedMappings.push(`Embed ${embedId}`);
                  }
                } else {
                  failedMappings.push(`Anchor ${i}`);
                }
              }
            } else {
              console.log("3. drawing1.xml or its .rels not found in ZIP.");
            }

            console.log("--- EXTRACTION SUMMARY ---");
            console.log(`Total images mapped: ${mappedCount}`);
            console.log(`Total images in workbook media: ${mediaCount}`);
            
            // Log first 5 rows' image data
            for (let i = 0; i < Math.min(5, rows.length); i++) {
               if (rows[i]) {
                 const imageColData = rows[i][27] ? (String(rows[i][27]).substring(0, 30) + '...') : String(rows[i][27]);
                 console.log(`Row ${i} | Col 27 (AB): ${imageColData}`);
               }
            }
            console.groupEnd();
          } catch (imgError) {
            console.error("JSZip image extraction failed with exception:", imgError);
            console.groupEnd();
          }
          
          detectSourceType(rows);
        } catch (err) {
          toast.error("Failed to parse Excel file");
          handleCancel();
        }
      };
      reader.readAsArrayBuffer(f);
    } else {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      handleCancel();
    }
  };

  const detectSourceType = (rows) => {
    setRawRowsArray(rows);
    if (!rows || rows.length === 0) return;

    const signatures = {
      Purchase: ['vendor', 'quantity', 'blumprice', 'verveprice', 'category'],
      'Blum Price': ['articleid', 'materialdescription', 'information', 'uom', 'moq', 'mrp'],
      'Price List': ['description', 'internaltype', 'shuttertype', 'width', 'height', 'depth', 'price', 'remarks']
    };

    const rowsToScan = rows.slice(0, 50);
    let rowScores = [];

    rowsToScan.forEach((row, idx) => {
      if (!Array.isArray(row)) return;
      
      const normalizedValues = row.map(v => 
        String(v).toLowerCase().trim().replace(/[^a-z0-9]/g, '')
      );

      let matchCounts = { Purchase: 0, 'Blum Price': 0, 'Price List': 0 };

      Object.keys(signatures).forEach(type => {
        signatures[type].forEach(sig => {
          if (normalizedValues.includes(sig)) {
            matchCounts[type]++;
          }
        });
      });

      const maxForThisRow = Math.max(matchCounts.Purchase, matchCounts['Blum Price'], matchCounts['Price List']);
      
      let typeForMax = null;
      let isTied = false;
      
      if (maxForThisRow > 0) {
        const topTypes = Object.keys(matchCounts).filter(k => matchCounts[k] === maxForThisRow);
        if (topTypes.length === 1) {
          typeForMax = topTypes[0];
        } else {
          isTied = true;
          typeForMax = topTypes[0]; // arbitrary pick for tie, but flagged
        }
      }

      rowScores.push({ idx, matchCounts, maxForThisRow, typeForMax, isTied });
    });

    rowScores.sort((a, b) => b.maxForThisRow - a.maxForThisRow);
    const bestRow = rowScores[0];

    if (bestRow && bestRow.maxForThisRow > 0) {
      setDetectedHeaderIndex(bestRow.idx);
      
      // Confidence Rules
      const isStrongMatch = bestRow.maxForThisRow >= 4 && !bestRow.isTied;
      
      if (isStrongMatch) {
        setDetectedType(bestRow.typeForMax);
        setNeedsManualSelection(false);
        processImportRows(rows, bestRow.idx, bestRow.typeForMax);
      } else {
        // Ambiguous or Low Match
        setDetectedType(bestRow.typeForMax); // Suggestion
        setNeedsManualSelection(true);
      }
    } else {
      // Low/No Match
      setDetectedHeaderIndex(0);
      setDetectedType('Purchase'); 
      setNeedsManualSelection(true);
    }
  };

  const handleManualSelection = (selectedType) => {
    setDetectedType(selectedType);
    setNeedsManualSelection(false);
    processImportRows(rawRowsArray, detectedHeaderIndex, selectedType);
  };

  const parseExcelDate = (excelDate) => {
    if (!excelDate) return "-";
    if (typeof excelDate === 'number') {
      return new Date(Math.round((excelDate - 25569) * 86400 * 1000)).toLocaleDateString('en-IN');
    }
    const parsed = new Date(excelDate);
    if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString('en-IN');
    return String(excelDate);
  };

  const processImportRows = async (rawArray, headerIdx, type) => {
    if (rawArray.length <= headerIdx + 1) {
      setPreview([]);
      return;
    }

    const headers = rawArray[headerIdx].map(h => String(h).trim());
    const dataRows = rawArray.slice(headerIdx + 1);
    
    // Map array rows to objects using the detected headers
    const rows = dataRows.map(rowArr => {
      let obj = {};
      headers.forEach((h, i) => {
        if (h) obj[h] = rowArr[i] !== undefined ? rowArr[i] : "";
      });
      obj._rawArray = rowArr; // Inject raw array for absolute column fallback
      return obj;
    });

    const validRows = [];

    rows.forEach((r, idx) => {
      // Ignore completely empty rows
      const hasAnyKey = Object.values(r).some(v => v !== "" && v !== null && v !== undefined && typeof v !== 'object');
      if (!hasAnyKey) return;

      const mapField = (keys) => {
        for (let k of keys) {
          // Try exact or case-insensitive match
          const exactKey = Object.keys(r).find(key => key !== '_rawArray' && key.toLowerCase() === k.toLowerCase());
          if (exactKey && r[exactKey] !== undefined && r[exactKey] !== '') return String(r[exactKey]);
        }
        return "-";
      };

      const mapNumber = (keys) => {
        for (let k of keys) {
          const exactKey = Object.keys(r).find(key => key.toLowerCase() === k.toLowerCase());
          if (exactKey && r[exactKey] !== undefined && r[exactKey] !== '') {
            const cleanStr = String(r[exactKey]).replace(/[^0-9.-]+/g, "");
            const parsed = Number(cleanStr);
            if (!isNaN(parsed) && cleanStr !== "") return parsed;
          }
        }
        return 0;
      };

      let dateStr = mapField(['Date', 'invoiceDate', 'invoice date']);
      
      // Look for the date field dynamically based on known keys, to parse it
      const dateKey = Object.keys(r).find(key => key.toLowerCase() === 'date' || key.toLowerCase() === 'invoicedate');
      if (dateStr !== "-" && dateKey && typeof r[dateKey] === 'number') {
        dateStr = parseExcelDate(r[dateKey]);
      }

      const imageBase64 = r._rawArray ? r._rawArray.find(val => typeof val === 'string' && val.startsWith('data:image')) : null;

      validRows.push({
        sourceType: type,
        category: mapField(['Category']),
        subCategory: mapField(['Sub Category']),
        articleId: mapField(['Article ID']),
        itemName: mapField(['itemName', 'Description']),
        description: mapField(['Description']),
        information: mapField(['Information']),
        internalType: mapField(['Internal Type']),
        shutterType: mapField(['Shutter Type']),
        width: mapField(['Width']),
        height: mapField(['Height']),
        depth: mapField(['Depth']),
        uom: mapField(['UOM', 'Unit']) !== "-" ? mapField(['UOM', 'Unit']) : 'nos',
        moq: mapNumber(['MOQ']),
        quantity: mapNumber(['Quantity', 'Qty']),
        vendor: mapField(['Vendor', 'Supplier']),
        blumPrice: mapNumber(['Blum Price']),
        vervePrice: mapNumber(['Verve Price']),
        remark: mapField(['Remarks', 'Remark']),
        image: mapField(['Image']) !== "-" ? mapField(['Image']) : (imageBase64 ? String(imageBase64) : "-")
      });
    });

    setPreview(validRows);
    setErrors([]); 
  };

  const handleCancel = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    setDetectedType(null);
    setNeedsManualSelection(false);
    setRawRowsArray([]);
  };

  const doUpload = async () => {
    if (preview.length === 0) {
      toast.error("No valid entries to upload.");
      return;
    }

    setLoading(true);
    setErrors([]); // clear previous errors

    try {
      // 1. Fetch next version
      let nextVersion = "";
      try {
        const vRes = await api.get('/v1/purchase-master/next-version');
        if (vRes.data && vRes.data.nextVersion) {
          nextVersion = vRes.data.nextVersion;
        } else {
          throw new Error("Invalid version response");
        }
      } catch (err) {
        toast.error("Failed to generate version. Please try again.");
        setLoading(false);
        return;
      }

      // 2. Append version and post in chunks to avoid payload limits
      const rowsToUpload = preview.map(r => {
        const payload = { ...r, version: nextVersion };
        // Remove "-" and empty strings to prevent Mongoose CastErrors on Date/ObjectId fields
        Object.keys(payload).forEach(k => {
          if (payload[k] === '-' || payload[k] === '') {
            delete payload[k];
          }
        });
        return payload;
      });
      const chunkSize = 50; 
      let totalSuccessCount = 0;
      let allErrors = [];

      console.log(`Initiating chunked upload: Total ${rowsToUpload.length} records in chunks of ${chunkSize}`);

      for (let i = 0; i < rowsToUpload.length; i += chunkSize) {
        const chunk = rowsToUpload.slice(i, i + chunkSize);
        try {
          const res = await api.post('/v1/purchase-master/bulk', chunk);
          const data = res?.data || res;
          
          console.log(`Chunk ${i / chunkSize + 1} response:`, data);

          if (data && data.errors && data.errors.length > 0) {
            allErrors.push(...data.errors);
          }
          
          const successCount = data?.count !== undefined ? Number(data.count) : (data?.insertedCount ? Number(data.insertedCount) : 0);
          if (!isNaN(successCount)) {
             totalSuccessCount += successCount;
          }
        } catch (chunkError) {
          console.error(`Chunk ${i / chunkSize + 1} failed:`, chunkError);
          allErrors.push(chunkError?.response?.data?.message || `Failed to upload chunk ${i / chunkSize + 1}`);
        }
      }

      console.log("Upload Complete. Total Success:", totalSuccessCount, "Errors:", allErrors.length);

      if (allErrors.length > 0) {
        toast.error(`Uploaded ${totalSuccessCount} records, but encountered some errors.`);
        console.error("Upload Errors:", allErrors);
        setErrors([{ row: 'Bulk', errors: allErrors.slice(0, 10) }]); // Show first 10 errors
      } else if (totalSuccessCount > 0) {
        toast.success(`Successfully processed ${totalSuccessCount} Records!`);
        handleCancel();
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error("No records were saved. Please check for errors.");
      }
    } catch (error) {
      console.error("Fatal upload error:", error);
      toast.error(error?.response?.data?.message || "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Master Data Upload"
      size="max-w-4xl"
    >
      <div className="space-y-4 fade-in">
        {file && (
          <div className="flex items-center justify-between bg-blue-50 p-4 rounded-md border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-md shadow-sm font-bold text-brand-blue capitalize">
                {file.name.split(".").pop()}
              </div>
              <div>
                <div className="font-bold text-sm text-brand-dark">
                  {file.name}
                </div>
                <div className="text-[10px] text-slate-500">
                  {(file.size / 1024).toFixed(1)} KB • Found{" "}
                  {preview.length} rows
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Manual Type Selection UI */}
        {file && needsManualSelection && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-md shadow-sm flex flex-col gap-3 animate-in fade-in zoom-in-95">
            <div className="flex items-center gap-2 text-amber-800 font-bold">
              <AlertTriangle size={18} /> Ambiguous or Low Confidence Match
            </div>
            <p className="text-sm text-amber-700">
              We couldn't confidently detect the exact layout of this file. 
              Please select the format that matches your data:
            </p>
            <div className="flex gap-2 mt-2">
              {['Purchase', 'Blum Price', 'Price List'].map(type => (
                <button
                  key={type}
                  onClick={() => handleManualSelection(type)}
                  className={`px-4 py-2 rounded-md font-medium text-sm transition-colors border ${
                    detectedType === type 
                    ? "bg-amber-600 text-white border-amber-700 shadow-md" 
                    : "bg-white text-amber-700 border-amber-300 hover:bg-amber-100"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Removed Version input */}

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-red-700 font-bold mb-3">
              <AlertTriangle size={18} /> Found {errors.length} Error(s)
            </div>
            <div className="max-h-[160px] overflow-y-auto space-y-2 pr-2 custom-scrollbar text-[11px] text-red-600">
              {errors.map((err, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-3 border-b border-red-100 pb-2 last:border-0"
                >
                  <div className="flex gap-3">
                    <span className="font-semibold w-16 shrink-0">
                      {err.row === 'DB' ? 'Upload DB:' : `Row ${err.row}:`}
                    </span>
                    <div>
                      {err.errors.map((msg, i) => (
                        <p key={i}>• {msg}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {preview.length > 0 && !needsManualSelection && (
        <div className="border border-slate-100 rounded-md overflow-hidden shadow-sm bg-white">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Preview ({preview.length})
            </span>
            {preview.length > 0 && (
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                Ready to Process
              </span>
            )}
          </div>
          {preview.length > 0 ? (
            <div className="overflow-x-auto">
              <Table
                headers={[
                  "Vendor", "Category", "Sub Category", "Article ID",
                  "Description", "Information",
                  "Internal Type", "Shutter Type", "Width", "Height", "Depth",
                  "UOM", "MOQ", "Quantity", "Blum Price", 
                  "Verve Price", "Image", "Remarks"
                ]}
                maxHeight="max-h-[300px]"
              >
                {preview.map((p, idx) => {
                  return (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors whitespace-nowrap">
                      <td className="td p-3 text-[13px] text-slate-600">{p.vendor || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.category || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.subCategory || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.articleId || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.description || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.information || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.internalType || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.shutterType || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.width || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.height || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.depth || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.uom || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.moq || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.quantity || '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.blumPrice ? fmt(p.blumPrice) : '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.vervePrice ? fmt(p.vervePrice) : '-'}</td>
                      <td className="td p-3 text-[13px] text-slate-600">
                        {p.image === 'HAS_IMAGE' ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : p.image && (p.image.startsWith('data:image') || p.image.startsWith('http')) ? (
                          <img src={p.image} alt="Thumbnail" className="w-8 h-8 object-cover rounded border border-slate-200" />
                        ) : p.image && p.image !== '-' ? (
                          <span className="truncate max-w-[100px] inline-block" title={p.image}>
                            {p.image}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="td p-3 text-[13px] text-slate-600">{p.remark || '-'}</td>
                    </tr>
                  );
                })}
              </Table>
            </div>
          ) : (
            <div className="p-6 text-center text-slate-500 text-sm">
              Please upload an Excel file to see preview.
            </div>
          )}
        </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
          <Button
            disabled={loading}
            onClick={() => {
              handleCancel();
              onClose();
            }}
            variant="secondary"
            className="flex-1 py-3"
          >
            Cancel
          </Button>
          <Button
            disabled={loading || preview.length === 0}
            onClick={doUpload}
            variant="primary"
            className="flex-1 py-3"
            loading={loading}
            leftIcon={!loading && <Check size={18} />}
          >
            Upload {preview.length} Records
          </Button>
        </div>
      </div>
    </Modal>
  );
};
